import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, User } from 'firebase/auth';
import { doc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { AdminUser } from '../types';

interface AuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Admin kullanıcısını kontrol et (eski sistem - admins collection)
  const checkAdminUser = async (userId: string): Promise<AdminUser | null> => {
    try {
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      if (adminDoc.exists()) {
        return adminDoc.data() as AdminUser;
      }
      return null;
    } catch (error) {
      console.error('Admin kullanıcı kontrolü hatası:', error);
      return null;
    }
  };

  // Yeni adminlerin şifre ile giriş kontrolü
  const checkPasswordLogin = async (email: string, password: string): Promise<AdminUser | null> => {
    try {
      // Önce admin_users collection'ından kontrol et
      const adminUsersQuery = query(
        collection(db, 'admin_users'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      
      const adminUsersSnapshot = await getDocs(adminUsersQuery);
      
      if (!adminUsersSnapshot.empty) {
        const adminDoc = adminUsersSnapshot.docs[0];
        const adminData = adminDoc.data();
        
        // Şifre kontrolü
        if (adminData.temporaryPassword === password) {
          console.log('✅ Yeni admin sistemi ile giriş başarılı');
          return {
            id: adminDoc.id,
            email: adminData.email,
            name: adminData.name,
            role: adminData.role,
            permissions: adminData.permissions || [],
            isActive: adminData.isActive,
            createdAt: adminData.createdAt,
            lastLoginAt: adminData.lastLoginAt
          } as AdminUser;
        }
      }
      
      // Sonra admins collection'ından şifre kontrolü
      const adminsQuery = query(
        collection(db, 'admins'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      
      const adminsSnapshot = await getDocs(adminsQuery);
      
      if (!adminsSnapshot.empty) {
        const adminDoc = adminsSnapshot.docs[0];
        const adminData = adminDoc.data();
        
        // Şifre kontrolü
        if (adminData.temporaryPassword === password) {
          console.log('✅ Eski admin sistemi ile giriş başarılı');
          return {
            id: adminDoc.id,
            email: adminData.email,
            name: adminData.name,
            role: adminData.role,
            permissions: adminData.permissions || [],
            isActive: adminData.isActive,
            createdAt: adminData.createdAt,
            lastLoginAt: adminData.lastLoginAt
          } as AdminUser;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Şifre ile giriş kontrolü hatası:', error);
      return null;
    }
  };

  // Email/password ile giriş (hybrid sistem)
  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Önce şifre ile admin kontrolü yap
      const passwordAdmin = await checkPasswordLogin(email, password);
      
      if (passwordAdmin) {
        // Mock user oluştur
        const mockUser = {
          uid: passwordAdmin.id,
          email: passwordAdmin.email,
          displayName: passwordAdmin.name,
          emailVerified: true
        } as User;
        
        setUser(mockUser);
        setAdminUser(passwordAdmin);
        console.log('✅ Admin şifre ile giriş başarılı:', passwordAdmin.name);
        return;
      }
      
      // Şifre başarısız olduysa Firebase Authentication dene (eski sistem)
      try {
        await signInWithEmailAndPassword(auth, email, password);
        // Firebase Authentication başarılı, admin kontrolü onAuthStateChanged'de yapılacak
      } catch (firebaseError) {
        throw new Error('Email veya şifre hatalı');
      }
    } catch (error) {
      console.error('Giriş hatası:', error);
      throw error;
    }
  };

  // Çıkış
  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUser(null);
      setAdminUser(null);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      throw error;
    }
  };

  // Firebase Authentication durumunu dinle (eski sistem)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      
      if (firebaseUser) {
        // Kullanıcı giriş yapmış, admin kontrolü yap
        const adminData = await checkAdminUser(firebaseUser.uid);
        
        if (adminData) {
          setUser(firebaseUser);
          setAdminUser(adminData);
        } else {
          // Admin değil, çıkış yap
          console.log('Bu kullanıcı admin değil:', firebaseUser.email);
          await signOut(auth);
          setUser(null);
          setAdminUser(null);
        }
      } else {
        // Kullanıcı çıkış yapmış (sadece Firebase Authentication için)
        // Şifre ile giriş yapmış adminlerin durumunu etkileme
        if (!user || !user.uid.startsWith('admin_')) {
          setUser(null);
          setAdminUser(null);
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const value: AuthContextType = {
    user,
    adminUser,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 